import { ColorTypes } from '@genial/design-tokens';
import { useFlag } from '@unleash/proxy-client-react';

import { FilterModalTypes } from '../../../../components/FilterModal';
import { DynamicCustomFiltersProps } from '../../../../store/product/types';

import { clubsConstants } from './components/Asset/products/Clubs/constants';
import { coesConstants } from './components/Asset/products/Coes/constants';
import { directTreasuryConstants } from './components/Asset/products/DirectTreasury/constants';
import { directTreasurySellConstants } from './components/Asset/products/DirectTreasurySell/constants';
import { equityConstants } from './components/Asset/products/Equity/constants';
import { equitySellConstants } from './components/Asset/products/EquitySell/constants';
import { fixedIncomeConstants } from './components/Asset/products/FixedIncome/constants';
import { fixedIncomeSellConstants } from './components/Asset/products/FixedIncomeSell/constants';
import { fundsConstants } from './components/Asset/products/Funds/constants';
import { fundsSellConstants } from './components/Asset/products/FundsSell/constants';
import { privatePensionConstants } from './components/Asset/products/PrivatePension/constants';
import { publicOfferConstants } from './components/Asset/products/PublicOffer/constants';
import { AssetCategoriesNormalized } from './components/AssetCategories';
import { AssetTabsTypes } from './components/AssetTabs';

export const transformCustomFilters = (
  customFilters: DynamicCustomFiltersProps,
  columns?: number
): FilterModalTypes.FilterType[] => {
  if (!customFilters || !customFilters.filters) {
    return [];
  }

  return [
    ...customFilters.filters.map(({ fieldName, label, options, extraProps }) => ({
      type: 'section' as const,
      fieldName,
      title: label,
      options,
      ...(columns ? { optionsColumns: columns, optionWidth: 'full' } : {}),
      ...extraProps,
    })),
  ];
};

export const transformCustomCategories = (
  customFilters: DynamicCustomFiltersProps,
  categoryFieldName: string
): AssetCategoriesNormalized[] => {
  const defaultCategory: AssetCategoriesNormalized = { title: 'Todos', value: '' };

  if (!customFilters || !customFilters.filters) {
    return [defaultCategory];
  }

  const category = customFilters.filters.find((filter) => filter.fieldName === categoryFieldName);

  if (!category) {
    return [defaultCategory];
  }

  return [
    defaultCategory,
    ...category.options.map(({ label, value }) => ({ title: label, value })),
  ];
};

export const productsConstants = [
  fixedIncomeConstants,
  directTreasuryConstants,
  fundsConstants,
  equityConstants,
  coesConstants,
] as const;

export const productsWithSellConstants = [
  ...productsConstants,
  directTreasurySellConstants,
  equitySellConstants,
  fixedIncomeSellConstants,
  fundsSellConstants,
] as const;

export const allProductsConstants = [
  ...productsConstants,
  publicOfferConstants,
  clubsConstants,
  privatePensionConstants,
] as const;

export const allConstants = [...allProductsConstants, ...productsWithSellConstants] as const;

export type ProductsConstantsType = (typeof allConstants)[number];
export type ProductsType = ProductsConstantsType['typeGetterId'];

export const getAllProductsEnabled = (
  productShelf: ProductsConstantsType[],
  unleashFlag: {
    [K in keyof ProductsConstantsType]: ProductsConstantsType[K] extends string ? K : never;
  }[keyof ProductsConstantsType]
) => {
  // Filtra os produtos de acordo com a flag do Unleash
  // e define o primeiro produto como selecionado
  const productsFiltered = productShelf
    .map((tab, index) => ({ selected: index === 0, ...tab }))
    .filter(({ [unleashFlag]: flag }) => !flag || useFlag(flag));

  // Faz o merge dos produtos com base no id e agrupa os produtos com o mesmo id
  // em um único objeto, mantendo as operações separadas
  const productsMerged = productsFiltered.reduce<AssetTabsTypes.AssetTabsProps[]>(
    (acc, product) => {
      const productIndex = acc.findIndex((p) => p.id === product.id);
      if (productIndex !== -1) {
        acc[productIndex].operationsTabs.push(product);
        return acc;
      }
      return [...acc, { ...product, operationsTabs: [product] }];
    },
    []
  );

  // Ordena as operações dentro de cada produto pelo operationId
  // e faz com que o primeiro elemento de cada produto seja o inicial
  const productsMergedSorted = productsMerged.map((product) => ({
    selected: product.selected,
    operationsTabs: product.operationsTabs.sort((a, b) => a.operationId - b.operationId),
    ...product.operationsTabs[0],
  }));

  return productsMergedSorted;
};

export const getProductColorFromType = (type: ProductsType): ColorTypes.Option => {
  const product = allConstants.find((p) => p.typeGetterId === type);
  if (!product) {
    return 'brand.primary.principal';
  }
  return product.color;
};
